import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLang } from "../i18n";
import ChapterHeading from "./ChapterHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const s = t.contact;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("prefill-contact", h);
    return () => window.removeEventListener("prefill-contact", h);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(s.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(s.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/20 focus:border-klein outline-none py-4 text-base placeholder:text-black/35 transition-colors duration-300 rounded-none";

  return (
    <section id="contact" data-testid="contact-section" className="py-32 md:py-48">
      <ChapterHeading num={s.num} title={s.title} note={s.note} testid="contact-heading" />
      <div className="px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/50 mb-4">Instagram</p>
          <a
            href="https://www.instagram.com/jeanne.leveque.m"
            target="_blank"
            rel="noreferrer"
            data-testid="contact-instagram-link"
            className="font-serif text-2xl md:text-4xl text-klein underline decoration-1 underline-offset-8 hover:text-terra transition-colors duration-300"
          >
            @jeanne.leveque.m
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-6 md:col-start-7 flex flex-col gap-8"
        >
          <input
            data-testid="contact-name-input"
            type="text"
            required
            maxLength={120}
            value={form.name}
            onChange={set("name")}
            placeholder={s.name}
            className={inputCls}
          />
          <input
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder={s.email}
            className={inputCls}
          />
          <textarea
            data-testid="contact-message-input"
            required
            maxLength={4000}
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder={s.message}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            className="group w-fit flex items-center gap-3 border border-black px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-klein hover:border-klein hover:text-linen disabled:opacity-50 disabled:pointer-events-none"
          >
            {sending ? s.sending : s.send}
            <Send size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
